/**
 * Add Entry Dialog Component
 * Modal for adding a new entry (line item) to a BOQ section — Studio theme
 * Includes catalog search integration
 */

import { Loader2, Search, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
	getCatalogProduct,
	type ProductSearchResult,
	searchCatalogProducts,
} from "@/features/catalog/services/catalog-api.service";
import {
	PRICE_ON_REQUEST,
	searchResultPriceLabel,
} from "@/features/catalog/utils/price-label";
import {
	FieldInput,
	FieldSelect,
	FloatPanel,
	StudioButton,
} from "@/features/studio/theme";
import {
	formatPricePaisa,
	paisaToRupeeInput,
	rupeeInputToPaisa,
} from "@/lib/format";
import { debounce } from "@/lib/utils";
import {
	addBOQEntry,
	type BOQSection,
} from "../../services/boq/boq-api.service";
import {
	getRoomSuggestions,
	type RoomReference,
} from "../../services/room-api.service";
import {
	isTruncatedVariantLabel,
	variantOptionLabel,
} from "./variant-option-label";

/**
 * The `item_kind` values a BOQ line may be drawn from.
 *
 * `boq_entries.catalog_product_id` is NOT NULL, so every line -- including a
 * labour line -- has to resolve to a catalogue row; that is the reason the
 * `item_kind` enum exists at all. The picker explicitly passes `material`,
 * `labour`, and `service` so that all billable items can be selected.
 *
 * `composite` is deliberately excluded. Composite rows (supply + install) have
 * no pricing model yet -- the rate card was deferred to plan 004 -- so offering
 * one here would put an unpriced line into a quote. Add it once there is a rate
 * to attach.
 */
const BOQ_ITEM_KINDS = ["material", "labour", "service"] as const;

interface AddEntryDialogProps {
	boqId: string;
	sections: BOQSection[];
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onSuccess: () => void;
	defaultSectionId?: string;
}

// ── Shared styled label ────────────────────────────────────────────────────────
interface VariantOption {
	id: string;
	label: string;
	/** The variant's customer price in paise, or null when it has none. */
	price: number | null;
}

/**
 * The product's active variants, named by their attributes (as the Selections
 * tab names them), each with its customer price from the detail response's
 * `prices`, the row the product page shows for that variant.
 */
function variantOptionsOf(
	detail: Awaited<ReturnType<typeof getCatalogProduct>>,
): VariantOption[] {
	return (
		(detail?.variants ?? [])
			// The same rule as getCatalogProduct: only a variant marked inactive is
			// left out.
			.filter((v) => v.isActive !== false)
			.map((v) => ({
				id: v.id,
				label:
					Object.values(v.variantAttributes ?? {}).join(", ") ||
					v.sku ||
					"Variant",
				price:
					detail?.prices?.find((p) => p.variantId === v.id)?.amount ?? null,
			}))
	);
}

function FieldLabel({
	children,
	htmlFor,
}: {
	children: React.ReactNode;
	htmlFor?: string;
}) {
	return (
		<label
			htmlFor={htmlFor}
			style={{
				display: "block",
				fontSize: 11,
				fontFamily: "var(--font-mono)",
				fontWeight: 600,
				letterSpacing: "0.09em",
				textTransform: "uppercase",
				color: "var(--color-dashboard-muted)",
				marginBottom: 6,
			}}
		>
			{children}
		</label>
	);
}

// ── Shared styled textarea ─────────────────────────────────────────────────────
function FieldTextarea({
	id,
	placeholder,
	value,
	onChange,
	rows = 2,
}: {
	id?: string;
	placeholder?: string;
	value: string;
	onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
	rows?: number;
}) {
	return (
		<textarea
			id={id}
			placeholder={placeholder}
			value={value}
			onChange={onChange}
			rows={rows}
			style={{
				width: "100%",
				padding: "10px 14px",
				fontSize: 14,
				fontFamily: "var(--font-body)",
				color: "var(--color-dashboard-text)",
				background: "var(--color-dashboard-paper)",
				border: "1px solid var(--color-dashboard-border)",
				borderRadius: 7,
				outline: "none",
				resize: "vertical",
				transition: "border-color 200ms, box-shadow 200ms",
				lineHeight: 1.5,
			}}
			onFocus={(e) => {
				e.currentTarget.style.borderColor = "var(--color-dashboard-accent)";
				e.currentTarget.style.boxShadow =
					"0 0 0 3px color-mix(in srgb, var(--color-dashboard-accent) 12%, transparent)";
			}}
			onBlur={(e) => {
				e.currentTarget.style.borderColor = "var(--color-dashboard-border)";
				e.currentTarget.style.boxShadow = "none";
			}}
		/>
	);
}

export function AddEntryDialog({
	boqId,
	sections,
	open,
	onOpenChange,
	onSuccess,
	defaultSectionId,
}: AddEntryDialogProps) {
	type CatalogSearchProduct = ProductSearchResult["products"][number];

	const [sectionId, setSectionId] = useState(defaultSectionId || "");
	const [searchQuery, setSearchQuery] = useState("");
	const [products, setProducts] = useState<CatalogSearchProduct[]>([]);
	const [isSearching, setIsSearching] = useState(false);
	const [selectedProduct, setSelectedProduct] =
		useState<CatalogSearchProduct | null>(null);
	const [description, setDescription] = useState("");
	const [quantity, setQuantity] = useState("1");
	/**
	 * What the chosen product or variant has no customer price on: "product" or
	 * "variant" when picking it filled in no unit price, else null. The hint it
	 * drives shows only while the unit price is still empty.
	 */
	const [unpricedPick, setUnpricedPick] = useState<
		"product" | "variant" | null
	>(null);
	// A product with active variants needs one chosen, as Shortlist does: the
	// BOQ line names what is bought, and a second line for the same product and
	// variant in a section is refused.
	const [variantOptions, setVariantOptions] = useState<VariantOption[]>([]);
	const [variantId, setVariantId] = useState("");
	const [loadingVariants, setLoadingVariants] = useState(false);
	const [variantsFailed, setVariantsFailed] = useState(false);
	// The product whose variants are being fetched, so a slow answer for a
	// product the designer has since replaced is ignored.
	const variantsFor = useRef<string | null>(null);
	const [unitPrice, setUnitPrice] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [showProductList, setShowProductList] = useState(false);

	// Room state
	const [roomQuery, setRoomQuery] = useState("");
	const [roomSuggestions, setRoomSuggestions] = useState<RoomReference[]>([]);
	const [selectedRoom, setSelectedRoom] = useState<RoomReference | null>(null);
	const [customRoomName, setCustomRoomName] = useState("");
	const [isCustomRoom, setIsCustomRoom] = useState(false);
	const [showRoomSuggestions, setShowRoomSuggestions] = useState(false);
	const roomListRef = useRef<HTMLDivElement>(null);

	const productListRef = useRef<HTMLDivElement>(null);
	const searchInputRef = useRef<HTMLInputElement>(null);

	// Initialize sectionId if not set
	useEffect(() => {
		if (!sectionId && sections.length > 0) {
			const defaultSec = sections.find((s) => s.isDefault) || sections[0];
			setSectionId(defaultSec.id);
		}
	}, [sections, sectionId]);

	// Close dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				productListRef.current &&
				!productListRef.current.contains(event.target as Node)
			) {
				setShowProductList(false);
			}
			if (
				roomListRef.current &&
				!roomListRef.current.contains(event.target as Node)
			) {
				setShowRoomSuggestions(false);
			}
		};

		if (showProductList || showRoomSuggestions) {
			document.addEventListener("mousedown", handleClickOutside);
		}

		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [showProductList, showRoomSuggestions]);

	// Fetch room suggestions when query changes (debounced)
	const fetchRoomsDebounced = useCallback(
		debounce(async (query: string) => {
			try {
				const rooms = await getRoomSuggestions(query);
				setRoomSuggestions(rooms);
			} catch (error) {
				console.error("Failed to load room suggestions:", error);
			}
		}, 300),
		[],
	);

	useEffect(() => {
		fetchRoomsDebounced(roomQuery);
	}, [roomQuery, fetchRoomsDebounced]);

	useEffect(() => {
		return () => {
			fetchRoomsDebounced.cancel();
		};
	}, [fetchRoomsDebounced]);

	const searchProducts = useCallback(
		debounce(async (query: string) => {
			setIsSearching(true);
			try {
				const result = await searchCatalogProducts({
					q: query || undefined,
					perPage: 10,
					itemKind: [...BOQ_ITEM_KINDS],
				});
				setProducts(result.products || []);
			} catch (error) {
				console.error("Search failed:", error);
			} finally {
				setIsSearching(false);
			}
		}, 300),
		[],
	);

	useEffect(() => {
		return () => {
			searchProducts.cancel();
		};
	}, [searchProducts]);

	// Close on Escape
	useEffect(() => {
		if (!open) return;
		const handle = (e: KeyboardEvent) => {
			if (e.key === "Escape") onOpenChange(false);
		};
		window.addEventListener("keydown", handle);
		return () => window.removeEventListener("keydown", handle);
	}, [open, onOpenChange]);

	// Load products when input is focused
	const handleInputFocus = async () => {
		setShowProductList(true);
		if (products.length === 0) {
			setIsSearching(true);
			try {
				const result = await searchCatalogProducts({
					perPage: 10,
					itemKind: [...BOQ_ITEM_KINDS],
				});
				setProducts(result.products || []);
			} catch (error) {
				console.error("Failed to load products:", error);
			} finally {
				setIsSearching(false);
			}
		}
	};

	// Trigger search when query changes
	useEffect(() => {
		if (showProductList) {
			searchProducts(searchQuery);
		}
	}, [searchQuery, searchProducts, showProductList]);

	const handleSelectProduct = async (product: CatalogSearchProduct) => {
		setSelectedProduct(product);
		setSearchQuery(product.name);
		setShowProductList(false);
		setVariantOptions([]);
		setVariantId("");
		setVariantsFailed(false);
		// The product's lowest customer price, in paise, until a variant is chosen.
		setUnitPrice(product.minPrice ? paisaToRupeeInput(product.minPrice) : "");
		setUnpricedPick(product.minPrice ? null : "product");

		variantsFor.current = product.id;
		if (!product.hasVariants) return;
		setLoadingVariants(true);
		try {
			const detail = await getCatalogProduct(product.id);
			if (variantsFor.current !== product.id) return;
			if (!detail) {
				// Without the variants the line cannot name one, so it is held
				// back until they load.
				setVariantsFailed(true);
				toast.error(
					"This product's variants couldn't be loaded. Pick the product again to retry.",
				);
				return;
			}
			// A product whose variants are all inactive has none to choose, and is
			// added without one, as before variants could be chosen here.
			setVariantOptions(variantOptionsOf(detail));
		} finally {
			if (variantsFor.current === product.id) setLoadingVariants(false);
		}
	};

	const handleSelectVariant = (id: string) => {
		setVariantId(id);
		// A variant with no customer price clears the field rather than keep
		// another variant's price, so the designer enters one.
		const price = variantOptions.find((v) => v.id === id)?.price;
		setUnitPrice(price ? paisaToRupeeInput(price) : "");
		setUnpricedPick(id && !price ? "variant" : null);
	};

	// The picker cuts a long variant label; the chosen one is spelled out under
	// it so the designer can still see which variant they picked.
	const chosenVariantLabel = variantOptions.find(
		(v) => v.id === variantId,
	)?.label;
	const chosenVariantFullLabel =
		chosenVariantLabel && isTruncatedVariantLabel(chosenVariantLabel)
			? chosenVariantLabel
			: null;

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!sectionId || !selectedProduct || !quantity || !unitPrice) {
			toast.error("Please fill in all required fields");
			return;
		}
		if (variantsFailed) {
			toast.error(
				"This product's variants couldn't be loaded. Pick the product again to retry.",
			);
			return;
		}
		if (variantOptions.length > 0 && !variantId) {
			toast.error("Choose a variant for this product");
			return;
		}

		const trimmedQuantity = quantity.trim();
		if (!/^[1-9]\d*$/.test(trimmedQuantity)) {
			toast.error("Quantity must be a whole number");
			return;
		}
		const parsedQuantity = parseInt(trimmedQuantity, 10);

		setIsSubmitting(true);
		try {
			const result = await addBOQEntry(boqId, sectionId, {
				catalogProductId: selectedProduct.id,
				catalogVariantId: variantId || undefined,
				roomReferenceId: isCustomRoom ? undefined : selectedRoom?.id,
				roomCustomName: isCustomRoom ? customRoomName.trim() : undefined,
				description: description.trim() || undefined,
				quantity: parsedQuantity,
				unitPrice: rupeeInputToPaisa(unitPrice),
			});

			if (result.success) {
				toast.success("Entry added successfully");
				resetForm();
				onSuccess();
				onOpenChange(false);
			} else {
				toast.error(result.error || "Failed to add entry");
			}
		} finally {
			setIsSubmitting(false);
		}
	};

	const resetForm = () => {
		setSelectedProduct(null);
		variantsFor.current = null;
		setVariantOptions([]);
		setVariantId("");
		setVariantsFailed(false);
		setLoadingVariants(false);
		setSearchQuery("");
		setDescription("");
		setQuantity("1");
		setUnitPrice("");
		setUnpricedPick(null);
		setShowProductList(false);
		setSelectedRoom(null);
		setCustomRoomName("");
		setIsCustomRoom(false);
		setRoomQuery("");
	};

	if (!open) return null;

	const sectionOptions = sections.map((s) => ({
		value: s.id,
		label: s.sectionName,
	}));

	return (
		<div
			role="dialog"
			aria-modal="true"
			style={{
				position: "fixed",
				inset: 0,
				zIndex: 200,
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				padding: "20px 16px",
				background: "rgba(30, 41, 59, 0.45)",
				backdropFilter: "blur(4px)",
			}}
			onClick={(e) => {
				if (e.target === e.currentTarget) onOpenChange(false);
			}}
			onKeyDown={(e) => {
				if (e.key === "Escape") onOpenChange(false);
			}}
		>
			<FloatPanel
				elevation={4}
				padding={0}
				style={{ width: "100%", maxWidth: 500, overflow: "visible" }}
			>
				{/* Header */}
				<div
					style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						padding: "20px 24px 16px",
						borderBottom: "1px solid var(--color-dashboard-border)",
					}}
				>
					<div>
						<p
							style={{
								fontSize: 10,
								fontFamily: "var(--font-mono)",
								fontWeight: 600,
								letterSpacing: "0.14em",
								textTransform: "uppercase",
								color: "var(--color-dashboard-accent-dark)",
								marginBottom: 4,
							}}
						>
							BOQ
						</p>
						<h2
							style={{
								fontFamily: "var(--font-display)",
								fontStyle: "italic",
								fontSize: 20,
								fontWeight: 500,
								color: "var(--color-dashboard-text)",
								letterSpacing: "-0.01em",
							}}
						>
							Add BOQ Entry
						</h2>
					</div>
					<button
						type="button"
						onClick={() => onOpenChange(false)}
						style={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							width: 32,
							height: 32,
							borderRadius: 6,
							border: "1px solid var(--color-dashboard-border)",
							background: "transparent",
							cursor: "pointer",
							color: "var(--color-dashboard-muted)",
							transition: "background 150ms",
						}}
						onMouseEnter={(e) =>
							((e.currentTarget as HTMLElement).style.background =
								"var(--color-dashboard-divider)")
						}
						onMouseLeave={(e) =>
							((e.currentTarget as HTMLElement).style.background =
								"transparent")
						}
					>
						<X size={15} strokeWidth={2} />
					</button>
				</div>

				{/* Body */}
				<form onSubmit={handleSubmit}>
					<div
						style={{
							padding: "20px 24px",
							display: "flex",
							flexDirection: "column",
							gap: 16,
							maxHeight: "70vh",
							overflowY: "auto",
						}}
					>
						<div>
							<FieldSelect
								label="Section *"
								options={sectionOptions}
								value={sectionId}
								onChange={(e) => setSectionId(e.target.value)}
							/>
						</div>

						{/* Room selector */}
						<div style={{ position: "relative" }} ref={roomListRef}>
							<FieldLabel>Room</FieldLabel>
							<div
								style={{
									border: "1px solid var(--color-dashboard-border)",
									borderRadius: 6,
									overflow: "hidden",
								}}
							>
								<input
									placeholder="Select or type room name..."
									value={
										isCustomRoom
											? customRoomName
											: selectedRoom?.name || roomQuery
									}
									onChange={(e) => {
										const val = e.target.value;
										setIsCustomRoom(true);
										setCustomRoomName(val);
										setRoomQuery(val);
										setSelectedRoom(null);
										setShowRoomSuggestions(true);
									}}
									onFocus={() => setShowRoomSuggestions(true)}
									style={{
										width: "100%",
										padding: "10px 14px",
										fontSize: 14,
										border: "none",
										outline: "none",
										fontFamily: "var(--font-body)",
										background: "var(--color-dashboard-paper)",
									}}
								/>
								{showRoomSuggestions && roomSuggestions.length > 0 && (
									<div
										style={{
											position: "absolute",
											top: "100%",
											left: 0,
											right: 0,
											background: "var(--color-dashboard-paper)",
											border: "1px solid var(--color-dashboard-border)",
											borderRadius: 6,
											boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
											zIndex: 10,
											maxHeight: 200,
											overflowY: "auto",
										}}
									>
										{roomSuggestions.map((room) => (
											<button
												type="button"
												key={room.id}
												onClick={() => {
													setSelectedRoom(room);
													setIsCustomRoom(false);
													setCustomRoomName("");
													setRoomQuery("");
													setShowRoomSuggestions(false);
												}}
												style={{
													display: "block",
													width: "100%",
													padding: "10px 14px",
													textAlign: "left",
													border: "none",
													background: "transparent",
													cursor: "pointer",
													fontFamily: "var(--font-body)",
													fontSize: 14,
													color: "var(--color-dashboard-text)",
												}}
												onMouseEnter={(e) =>
													((e.currentTarget as HTMLElement).style.background =
														"var(--color-dashboard-divider)")
												}
												onMouseLeave={(e) =>
													((e.currentTarget as HTMLElement).style.background =
														"transparent")
												}
											>
												{room.name}
												{room.isDefault && (
													<span
														style={{
															marginLeft: 8,
															fontSize: 10,
															color: "var(--color-dashboard-muted)",
															textTransform: "uppercase",
														}}
													>
														Default
													</span>
												)}
											</button>
										))}
									</div>
								)}
							</div>
							{selectedRoom && !isCustomRoom && (
								<div
									style={{
										marginTop: 6,
										fontSize: 11,
										color: "var(--color-dashboard-muted)",
									}}
								>
									Selected: {selectedRoom.name}{" "}
									<button
										type="button"
										onClick={() => {
											setSelectedRoom(null);
											setIsCustomRoom(true);
										}}
										style={{
											background: "none",
											border: "none",
											cursor: "pointer",
											fontSize: 11,
											color: "var(--color-dashboard-accent-dark)",
											textDecoration: "underline",
										}}
									>
										Clear
									</button>
								</div>
							)}
						</div>

						<div style={{ position: "relative" }} ref={productListRef}>
							<FieldLabel htmlFor="product-search-input">
								Product Search *
							</FieldLabel>
							<div style={{ position: "relative" }}>
								<Search
									size={16}
									style={{
										position: "absolute",
										left: 14,
										top: 12,
										color: "var(--color-dashboard-muted)",
									}}
								/>
								<input
									id="product-search-input"
									ref={searchInputRef}
									placeholder="Search materials, furniture, fixtures..."
									value={searchQuery}
									onChange={(e) => {
										setSearchQuery(e.target.value);
										if (
											selectedProduct &&
											e.target.value !== selectedProduct.name
										) {
											setSelectedProduct(null);
											setUnpricedPick(null);
										}
									}}
									onFocus={handleInputFocus}
									required
									style={{
										width: "100%",
										height: 40,
										paddingLeft: 38,
										paddingRight: 38,
										fontSize: 14,
										fontFamily: "var(--font-body)",
										color: "var(--color-dashboard-text)",
										background: "var(--color-dashboard-paper)",
										border: "1px solid var(--color-dashboard-border)",
										borderRadius: 7,
										outline: "none",
										transition: "border-color 200ms, box-shadow 200ms",
									}}
									onFocusCapture={(e) => {
										e.currentTarget.style.borderColor =
											"var(--color-dashboard-accent)";
										e.currentTarget.style.boxShadow =
											"0 0 0 3px color-mix(in srgb, var(--color-dashboard-accent) 12%, transparent)";
									}}
									onBlurCapture={(e) => {
										e.currentTarget.style.borderColor =
											"var(--color-dashboard-border)";
										e.currentTarget.style.boxShadow = "none";
									}}
								/>
								{isSearching && (
									<Loader2
										size={16}
										className="text-dashboard-muted"
										style={{
											position: "absolute",
											right: 14,
											top: 12,
											animation: "spin 1s linear infinite",
										}}
									/>
								)}
							</div>

							{showProductList && products.length > 0 && (
								<div
									style={{
										position: "absolute",
										zIndex: 50,
										width: "100%",
										marginTop: 4,
										background: "var(--color-dashboard-paper)",
										border: "1px solid var(--color-dashboard-border)",
										borderRadius: 7,
										boxShadow:
											"0 4px 12px color-mix(in srgb, var(--color-dashboard-shadow-ink) 8%, transparent)",
										maxHeight: 200,
										overflowY: "auto",
									}}
								>
									{products.map((product) => (
										<button
											key={product.id}
											type="button"
											style={{
												width: "100%",
												textAlign: "left",
												padding: "10px 14px",
												display: "flex",
												justifyContent: "space-between",
												alignItems: "center",
												borderBottom:
													"1px solid var(--color-dashboard-border-soft)",
												background: "transparent",
												cursor: "pointer",
												transition: "background 150ms",
											}}
											onMouseEnter={(e) =>
												((e.currentTarget as HTMLElement).style.background =
													"var(--color-dashboard-divider)")
											}
											onMouseLeave={(e) =>
												((e.currentTarget as HTMLElement).style.background =
													"transparent")
											}
											onClick={() => handleSelectProduct(product)}
										>
											<div>
												<div
													style={{
														fontWeight: 600,
														color: "var(--color-dashboard-text)",
														fontSize: 13.5,
													}}
												>
													{product.name}
												</div>
												<div
													style={{
														fontSize: 11,
														color: "var(--color-dashboard-muted)",
													}}
												>
													{product.brandName} • {product.categoryName}
												</div>
											</div>
											<div
												style={{
													fontSize: 11,
													fontWeight: 700,
													background: "var(--color-dashboard-divider)",
													color: "var(--color-dashboard-accent-dark)",
													padding: "4px 8px",
													borderRadius: 4,
												}}
											>
												{searchResultPriceLabel(product) ?? PRICE_ON_REQUEST}
											</div>
										</button>
									))}
								</div>
							)}
						</div>

						{selectedProduct && (
							<div
								style={{
									display: "flex",
									alignItems: "start",
									gap: 12,
									padding: 12,
									background: "var(--color-dashboard-paper)",
									border: "1px solid var(--color-dashboard-border)",
									borderRadius: 7,
								}}
							>
								{selectedProduct.primaryImage ? (
									<img
										src={selectedProduct.primaryImage}
										alt=""
										style={{
											width: 48,
											height: 48,
											borderRadius: 4,
											objectFit: "cover",
											border: "1px solid var(--color-dashboard-border)",
										}}
									/>
								) : (
									<div
										style={{
											width: 48,
											height: 48,
											borderRadius: 4,
											background: "var(--color-dashboard-divider)",
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											border: "1px solid var(--color-dashboard-border)",
										}}
									>
										<Search
											size={20}
											style={{ color: "var(--color-dashboard-muted)" }}
										/>
									</div>
								)}
								<div style={{ flex: 1, minWidth: 0 }}>
									<p
										style={{
											fontSize: 9,
											fontFamily: "var(--font-mono)",
											fontWeight: 600,
											letterSpacing: "0.08em",
											textTransform: "uppercase",
											color: "var(--color-dashboard-accent-dark)",
											marginBottom: 2,
										}}
									>
										Selected Product
									</p>
									<p
										style={{
											fontSize: 13.5,
											fontWeight: 600,
											color: "var(--color-dashboard-text)",
											margin: 0,
											whiteSpace: "nowrap",
											overflow: "hidden",
											textOverflow: "ellipsis",
										}}
									>
										{selectedProduct.name}
									</p>
									<p
										style={{
											fontSize: 12,
											color: "var(--color-dashboard-muted)",
											margin: 0,
										}}
									>
										{selectedProduct.brandName} • {selectedProduct.categoryName}
									</p>
								</div>
							</div>
						)}

						{selectedProduct?.hasVariants &&
							(loadingVariants ? (
								<p
									style={{
										fontSize: 12,
										color: "var(--color-dashboard-muted)",
										margin: 0,
									}}
								>
									Loading variants…
								</p>
							) : variantOptions.length > 0 ? (
								<FieldSelect
									label="Variant *"
									id="variant-select"
									options={[
										{ value: "", label: "Choose a variant" },
										...variantOptions.map((v) => ({
											value: v.id,
											label: variantOptionLabel(
												v.label,
												v.price ? formatPricePaisa(v.price) : PRICE_ON_REQUEST,
											),
										})),
									]}
									value={variantId}
									onChange={(e) => handleSelectVariant(e.target.value)}
								/>
							) : null)}
						{chosenVariantFullLabel && (
							<p
								data-testid="variant-full-label"
								style={{
									fontSize: 12,
									color: "var(--color-dashboard-muted)",
									margin: 0,
									overflowWrap: "anywhere",
								}}
							>
								{chosenVariantFullLabel}
							</p>
						)}

						<div
							style={{
								display: "grid",
								gridTemplateColumns:
									"repeat(auto-fit, minmax(min(100%, 160px), 1fr))",
								gap: 16,
							}}
						>
							<div>
								<FieldInput
									label="Quantity *"
									id="quantity-input"
									type="number"
									min="1"
									step="1"
									value={quantity}
									onChange={(e) => setQuantity(e.target.value)}
									required
								/>
							</div>
							<div>
								<FieldInput
									label="Unit Price (₹) *"
									id="unit-price-input"
									type="number"
									min="0"
									step="0.01"
									value={unitPrice}
									onChange={(e) => setUnitPrice(e.target.value)}
									required
								/>
								<p
									style={{
										fontSize: 10,
										color: "var(--color-dashboard-muted)",
										marginTop: 4,
										fontWeight: 500,
									}}
								>
									= {formatPricePaisa(rupeeInputToPaisa(unitPrice || "0"))} per
									unit
								</p>
								{unpricedPick && !unitPrice && (
									<p
										style={{
											fontSize: 11,
											color: "var(--color-dashboard-text)",
											marginTop: 4,
										}}
									>
										This {unpricedPick} has no catalogue price. Enter the unit
										price.
									</p>
								)}
							</div>
						</div>

						<div>
							<FieldLabel htmlFor="entry-desc-input">
								Site Notes (optional)
							</FieldLabel>
							<FieldTextarea
								id="entry-desc-input"
								placeholder="e.g. For master bedroom only, verified with client"
								value={description}
								onChange={(e) => setDescription(e.target.value)}
								rows={2}
							/>
						</div>
					</div>

					{/* Footer */}
					<div
						style={{
							display: "flex",
							justifyContent: "flex-end",
							gap: 10,
							padding: "16px 24px",
							borderTop: "1px solid var(--color-dashboard-border)",
							background: "var(--color-dashboard-surface)",
						}}
					>
						<StudioButton
							variant="secondary"
							size="sm"
							type="button"
							onClick={() => onOpenChange(false)}
						>
							Cancel
						</StudioButton>
						<StudioButton
							variant="primary"
							size="sm"
							type="submit"
							disabled={
								isSubmitting ||
								loadingVariants ||
								!selectedProduct ||
								!quantity ||
								!unitPrice ||
								!sectionId
							}
						>
							{isSubmitting ? "Adding…" : "Add to BOQ"}
						</StudioButton>
					</div>
				</form>
			</FloatPanel>
		</div>
	);
}
